import { useEffect, useMemo, useRef, type ReactNode, type MutableRefObject } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MicOff, Pin, PinOff, VideoOff } from "lucide-react";

export interface GridParticipantTile {
  id: string;
  stream: MediaStream | null;
  name?: string;
  isLocal?: boolean;
  isVideoEnabled?: boolean;
  isAudioEnabled?: boolean;
}

interface ParticipantVideoGridProps {
  participants: GridParticipantTile[];
  activeSpeaker?: string | null;
  spotlightedParticipant?: string | null;
  onSpotlightChange?: (participantId: string | null) => void;
  renderLocalActions?: (participant: GridParticipantTile) => ReactNode;
  renderSpeakingTime?: (participantId: string) => ReactNode;
  localVideoRef?: MutableRefObject<HTMLVideoElement | null>;
}

export function ParticipantVideoGrid({
  participants,
  activeSpeaker,
  spotlightedParticipant,
  onSpotlightChange,
  renderLocalActions,
  renderSpeakingTime,
  localVideoRef,
}: ParticipantVideoGridProps) {
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  useEffect(() => {
    participants.forEach((participant) => {
      if (!participant.stream) return;
      const videoElement = videoRefs.current.get(participant.id);
      if (videoElement && videoElement.srcObject !== participant.stream) {
        videoElement.srcObject = participant.stream;
      }
    });
  }, [participants]);

  const orderedParticipants = useMemo(() => {
    if (!spotlightedParticipant) return participants;
    const spotlightTile = participants.find((p) => p.id === spotlightedParticipant);
    const others = participants.filter((p) => p.id !== spotlightedParticipant);
    return spotlightTile ? [spotlightTile, ...others] : participants;
  }, [participants, spotlightedParticipant]);

  if (participants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-[28px] border border-dashed border-border/70 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.10),transparent_35%),hsl(var(--muted)/0.35)]">
        <div className="space-y-2 text-center">
          <p className="text-lg font-semibold text-foreground">Waiting for participants</p>
          <p className="text-sm text-muted-foreground">Your room is ready. People will appear here as they join.</p>
        </div>
      </div>
    );
  }

  if (spotlightedParticipant && orderedParticipants[0].id === spotlightedParticipant) {
    const spotlightTile = orderedParticipants[0];
    const secondaryTiles = orderedParticipants.slice(1);
    const isActiveSpeaker = activeSpeaker === spotlightTile.id;

    return (
      <div className="flex h-full flex-col gap-3 lg:gap-4">
        <Card
          className={`relative flex-1 min-h-[220px] overflow-hidden rounded-[28px] border border-border/60 bg-[linear-gradient(180deg,hsl(var(--card)),hsl(var(--muted)/0.30))] transition-all md:min-h-[320px] lg:min-h-[420px] xl:min-h-[520px] ${
            isActiveSpeaker ? "ring-4 ring-primary shadow-2xl shadow-primary/30" : "shadow-lg shadow-black/5"
          }`}
        >
          {spotlightTile.isVideoEnabled !== false && spotlightTile.stream ? (
            <video
              ref={(el) => {
                if (!el) return;
                if (spotlightTile.isLocal && localVideoRef) {
                  localVideoRef.current = el;
                }
                videoRefs.current.set(spotlightTile.id, el);
                if (el.srcObject !== spotlightTile.stream) {
                  el.srcObject = spotlightTile.stream;
                }
              }}
              className="h-full w-full bg-black object-contain"
              autoPlay
              playsInline
              muted={spotlightTile.isLocal}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.18),transparent_35%),linear-gradient(180deg,hsl(var(--muted)/0.60),hsl(var(--muted)/0.20))]">
              <div className="space-y-3 text-center">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-primary/20 bg-background/80 shadow-lg md:h-32 md:w-32">
                  <span className="text-4xl font-semibold text-primary md:text-5xl">
                    {spotlightTile.name?.[0]?.toUpperCase() || "U"}
                  </span>
                </div>
                <p className="text-lg text-muted-foreground">{spotlightTile.name || "Participant"}</p>
              </div>
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />

          {renderLocalActions && spotlightTile.isLocal && (
            <div className="absolute right-16 top-3 flex gap-2">
              {renderLocalActions(spotlightTile)}
            </div>
          )}

          <div className="absolute right-3 top-3 flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => onSpotlightChange?.(null)}
                  className="h-9 w-9 rounded-full border border-border/60 bg-background/85 hover:bg-background"
                >
                  <PinOff className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Unpin participant</TooltipContent>
            </Tooltip>
          </div>

          <div
            className={`absolute bottom-4 left-4 flex items-center space-x-2 rounded-full px-3 py-1.5 text-sm ${
              isActiveSpeaker ? "bg-primary text-primary-foreground" : "bg-background/85 text-foreground"
            }`}
          >
            <Pin className="h-3.5 w-3.5" />
            <span>{spotlightTile.name || "Participant"}</span>
            {spotlightTile.isAudioEnabled === false && <MicOff className="h-3.5 w-3.5" />}
          </div>

          {renderSpeakingTime && (
            <div className="absolute bottom-4 right-4">
              {renderSpeakingTime(spotlightTile.id)}
            </div>
          )}
        </Card>

        {secondaryTiles.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 lg:grid-cols-4 lg:gap-4">
            {secondaryTiles.map((tile) => {
              const isActive = activeSpeaker === tile.id;
              const hasVideo = tile.isVideoEnabled !== false && Boolean(tile.stream);

              return (
                <Card
                  key={tile.id}
                  className={`group relative min-h-[140px] cursor-pointer overflow-hidden rounded-[24px] border border-border/60 bg-[linear-gradient(180deg,hsl(var(--card)),hsl(var(--muted)/0.25))] transition-all sm:min-h-[180px] ${
                    isActive ? "ring-2 ring-primary shadow-xl shadow-primary/20" : "shadow-sm"
                  }`}
                  onClick={() => onSpotlightChange?.(tile.id)}
                >
                  {hasVideo ? (
                    <video
                      ref={(el) => {
                        if (!el) return;
                        if (tile.isLocal && localVideoRef) {
                          localVideoRef.current = el;
                        }
                        videoRefs.current.set(tile.id, el);
                        if (tile.stream && el.srcObject !== tile.stream) {
                          el.srcObject = tile.stream;
                        }
                      }}
                      className="h-full w-full bg-black object-contain"
                      autoPlay
                      playsInline
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.14),transparent_35%),linear-gradient(180deg,hsl(var(--muted)/0.60),hsl(var(--muted)/0.20))]">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/20 bg-background/80">
                        <span className="text-lg font-semibold text-primary">{tile.name?.[0]?.toUpperCase() || "U"}</span>
                      </div>
                    </div>
                  )}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div
                    className={`absolute bottom-3 left-3 flex items-center space-x-1 rounded-full px-2.5 py-1 text-xs ${
                      isActive ? "bg-primary text-primary-foreground" : "bg-background/85 text-foreground"
                    }`}
                  >
                    <span className="max-w-[120px] truncate">{tile.name || "Participant"}</span>
                    {tile.isAudioEnabled === false && <MicOff className="h-3 w-3" />}
                  </div>

                  {renderSpeakingTime && (
                    <div className="absolute bottom-3 right-3">
                      {renderSpeakingTime(tile.id)}
                    </div>
                  )}

                  <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 rounded-full border border-border/60 bg-background/85 hover:bg-background"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSpotlightChange?.(tile.id);
                          }}
                        >
                          <Pin className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Pin participant</TooltipContent>
                    </Tooltip>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const gridClass =
    participants.length === 1
      ? "grid-cols-1"
      : participants.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : participants.length <= 4
      ? "grid-cols-2"
      : participants.length <= 6
      ? "grid-cols-2 md:grid-cols-3"
      : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  return (
    <div className={`grid ${gridClass} h-full w-full auto-rows-fr gap-2 sm:gap-3 md:gap-4`}>
      {orderedParticipants.map((tile) => {
        const hasVideo = tile.isVideoEnabled !== false && Boolean(tile.stream);
        const isActive = activeSpeaker === tile.id;

        return (
          <Card
            key={tile.id}
            className={`group relative min-h-[150px] cursor-pointer overflow-hidden rounded-[24px] border border-border/60 bg-[linear-gradient(180deg,hsl(var(--card)),hsl(var(--muted)/0.28))] transition-all duration-300 hover:ring-2 hover:ring-primary/40 sm:min-h-[180px] md:min-h-[220px] lg:min-h-[260px] ${
              isActive ? "ring-4 ring-primary shadow-2xl shadow-primary/25" : "shadow-sm"
            }`}
            onClick={() => onSpotlightChange?.(tile.id)}
          >
            {hasVideo ? (
              <video
                ref={(el) => {
                  if (!el) return;
                  if (tile.isLocal && localVideoRef) {
                    localVideoRef.current = el;
                  }
                  videoRefs.current.set(tile.id, el);
                  if (tile.stream && el.srcObject !== tile.stream) {
                    el.srcObject = tile.stream;
                  }
                }}
                className="h-full w-full bg-black object-contain"
                autoPlay
                playsInline
                muted={tile.isLocal}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.14),transparent_35%),linear-gradient(180deg,hsl(var(--muted)/0.60),hsl(var(--muted)/0.20))]">
                <div className="text-center">
                  <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-background/80">
                    <span className="text-xl font-semibold text-primary">{tile.name?.[0]?.toUpperCase() || "U"}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{tile.name || "Participant"}</p>
                </div>
              </div>
            )}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
            <div
              className={`absolute bottom-3 left-3 flex items-center space-x-1 rounded-full px-2.5 py-1 text-xs transition-all ${
                isActive ? "bg-primary text-primary-foreground" : "bg-background/85 text-foreground"
              }`}
            >
              <span className="max-w-[160px] truncate">{tile.name || "Participant"}</span>
              {tile.isAudioEnabled === false && <MicOff className="h-3 w-3" />}
              {tile.isVideoEnabled === false && <VideoOff className="h-3 w-3" />}
            </div>

            {renderLocalActions && tile.isLocal && (
              <div className="absolute right-14 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                {renderLocalActions(tile)}
              </div>
            )}

            {renderSpeakingTime && (
              <div className="absolute bottom-3 right-3">
                {renderSpeakingTime(tile.id)}
              </div>
            )}

            <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 rounded-full border border-border/60 bg-background/85 hover:bg-background"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSpotlightChange?.(tile.id);
                    }}
                  >
                    <Pin className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pin participant</TooltipContent>
              </Tooltip>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
